/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import {
  originalSessionStorage,
  getInMemorySessionStorage,
  hasConsent,
} from './sessionStorage-in-memory';

export {
  migrateInMemoryToSessionStorage,
  clearInMemorySessionStorage,
  getInMemorySessionStorageContents,
} from './sessionStorage-in-memory';

/**
 * Selects the active sessionStorage backend based on CDU consent: real
 * sessionStorage when consent is accepted, otherwise in-memory storage.
 *
 * @returns The Storage backend to operate on.
 */
function sessionBackend(): Storage {
  return hasConsent() ? originalSessionStorage : getInMemorySessionStorage();
}

/**
 * Creates a sessionStorage wrapper that enforces CDU consent.
 *
 * Rules:
 * - When consent is PENDING: Use in-memory storage (RAM only, no persistence)
 * - When consent is ACCEPTED: Use real sessionStorage (persists during session)
 * - When consent is REJECTED: Use in-memory storage (RAM only, no persistence)
 *
 * Legal Basis: When consent is rejected, in-memory storage is allowed as it's
 * temporary, non-persistent, and necessary for application functionality during
 * the current session. Data is lost on page reload or browser close.
 *
 * @returns {Storage} Wrapped sessionStorage object
 */
export function createSessionStorageWrapper(): Storage {
  return {
    getItem: (key: string): string | null => sessionBackend().getItem(key),
    setItem: (key: string, value: string): void => sessionBackend().setItem(key, value),
    removeItem: (key: string): void => sessionBackend().removeItem(key),
    clear: (): void => sessionBackend().clear(),
    get length(): number {
      return sessionBackend().length;
    },
    key: (index: number): string | null => sessionBackend().key(index),
  };
}

/**
 * Initializes the sessionStorage wrapper by replacing window.sessionStorage.
 * This should be called as early as possible in the application lifecycle,
 * before any other code that uses sessionStorage.
 *
 * @returns {void}
 */
export function initializeSessionStorageWrapper(): void {
  if ((window as any).__originalSessionStorage) {
    return;
  }

  (window as any).__originalSessionStorage = originalSessionStorage;

  Object.defineProperty(window, 'sessionStorage', {
    writable: true,
    configurable: true,
    value: createSessionStorageWrapper(),
  });

  console.log('[SessionStorageWrapper] Initialized - using in-memory storage until CDU consent is accepted');
}
